import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Pause, Play, Trash2, RefreshCw, Megaphone, ChevronDown, ChevronRight, Rocket, Download, AlertTriangle, Copy, Pencil, Lightbulb } from 'lucide-react';
import { fetchCampaigns, fetchAdAccounts, updateCampaign, deleteCampaign, duplicateCampaign, fetchCampaignInsights, fetchAds, fetchAdSets, updateAd, publishAd } from '../../redux/metaAdsSlice';
import { formatCurrency } from '../../utils/currency';
import MetaAdsCampaignWizard from './MetaAdsCampaignWizard';
import { RenameCampaignDialog, EditAdSetDialog, EditAdDialog } from './MetaAdsEditDialogs';
import api from '../../services/api';
import alertService from '../../utils/alert';

// Meta's delivery state, in plain words. `status` is only what was requested;
// this is what's actually happening (an "active" ad can still be rejected).
const DELIVERY = {
  ACTIVE: { label: 'Delivering', cls: 'bg-[#E3F3E8] text-[#036B3A]' },
  PAUSED: { label: 'Paused', cls: 'bg-[#F4F6F9] text-[#5C5C5C]' },
  CAMPAIGN_PAUSED: { label: 'Campaign paused', cls: 'bg-[#F4F6F9] text-[#5C5C5C]' },
  ADSET_PAUSED: { label: 'Ad set paused', cls: 'bg-[#F4F6F9] text-[#5C5C5C]' },
  IN_PROCESS: { label: 'Processing', cls: 'bg-[#EAF4FE] text-[#0176D3]' },
  PENDING_REVIEW: { label: 'In review', cls: 'bg-[#EAF4FE] text-[#0176D3]' },
  DISAPPROVED: { label: 'Rejected', cls: 'bg-[#FDE7E9] text-[#8E030F]' },
  WITH_ISSUES: { label: 'Has issues', cls: 'bg-[#FFF4E5] text-[#8C4B02]' },
  PENDING_BILLING_INFO: { label: 'Needs billing info', cls: 'bg-[#FFF4E5] text-[#8C4B02]' },
  ARCHIVED: { label: 'Archived', cls: 'bg-[#F4F6F9] text-[#5C5C5C]' },
  DELETED: { label: 'Deleted', cls: 'bg-[#F4F6F9] text-[#5C5C5C]' },
};

function DeliveryBadge({ effectiveStatus, fallback }) {
  const d = DELIVERY[effectiveStatus];
  const label = d?.label || effectiveStatus || fallback;
  const cls = d?.cls || (fallback === 'active' ? 'bg-[#E3F3E8] text-[#036B3A]' : 'bg-[#F4F6F9] text-[#5C5C5C]');
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{label}</span>;
}

// Meta's own learning-phase status (learning_stage_info.status) — materially
// changes how CTR/CPL should be read while it's showing. SUCCESS (exited,
// stable) intentionally renders nothing — that's the normal, unremarkable
// state and doesn't need a badge cluttering every stable ad set.
const LEARNING = {
  LEARNING: { label: 'Learning', cls: 'bg-[#EAF4FE] text-[#0176D3]', title: 'Meta is still gathering data on this ad set — results may be unstable until it exits learning.' },
  FAIL: { label: 'Learning Limited', cls: 'bg-[#FFF4E5] text-[#8C4B02]', title: 'This ad set has too little data to reliably exit learning — its performance numbers may never fully stabilize. Consider a broader audience or a higher budget.' },
};

function LearningBadge({ learningStatus }) {
  const l = LEARNING[learningStatus];
  if (!l) return null;
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${l.cls}`} title={l.title}>{l.label}</span>;
}

function AdRow({ campaignId, ad }) {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const isLive = ad.status === 'active';
  const canPublish = ad.platformAdId && !isLive;

  const publish = async () => {
    // Publishing is the step that starts spending money, so ask first and say what it will cost.
    const confirmed = await alertService.confirm(
      'Publish this ad?',
      `"${ad.name}" will start running on Meta and spending your ad budget once Meta approves it. Pause it any time to stop.`,
      'warning',
      'Publish',
    );
    if (!confirmed) return;
    setBusy(true);
    const result = await dispatch(publishAd({ campaignId, id: ad._id }));
    setBusy(false);
    if (result.error) alertService.error('Could not publish', result.payload || 'Meta did not accept the request. Please try again.');
  };

  const pause = async () => {
    setBusy(true);
    const result = await dispatch(updateAd({ campaignId, id: ad._id, updates: { status: 'paused' } }));
    setBusy(false);
    if (result.error) alertService.error('Could not pause', result.payload || 'Meta did not accept the request. Please try again.');
  };

  return (
    <div className="flex items-center justify-between py-2 px-4 border-t border-[#EAEDF1]">
      <div className="min-w-0">
        <div className="text-[12px] font-semibold text-[#2E2E2E]">{ad.name}</div>
        <div className="text-[11px] text-[#5C5C5C]">{ad.creative?.headline || 'No headline'}</div>
        {(ad.reviewIssues || []).length > 0 && (
          <div className="mt-1 flex items-start gap-1 text-[11px] text-[#8E030F]">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>{ad.reviewIssues.join(' · ')}</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <DeliveryBadge effectiveStatus={ad.effectiveStatus} fallback={ad.status} />
        <button type="button" onClick={() => setEditing(true)} aria-label={`Edit ${(ad.name || '').trim()}`} title="Edit ad" className="p-1 rounded hover:bg-[#F3F2F2] text-[#706E6B]"><Pencil size={13} /></button>
        {canPublish ? (
          <button onClick={publish} disabled={busy} className="flex items-center gap-1 text-[11px] font-semibold text-white bg-[#2E844A] hover:bg-[#24703C] disabled:opacity-50 px-2.5 py-1 rounded-[4px] whitespace-nowrap">
            <Rocket size={12} /> {busy ? 'Publishing…' : 'Publish'}
          </button>
        ) : isLive ? (
          <button onClick={pause} disabled={busy} className="flex items-center gap-1 text-[11px] font-semibold text-[#5C5C5C] hover:text-[#2E2E2E] border border-[#D8DDE6] px-2.5 py-1 rounded-[4px] disabled:opacity-50 whitespace-nowrap">
            <Pause size={12} /> {busy ? 'Pausing…' : 'Pause'}
          </button>
        ) : null}
      </div>
      {editing && <EditAdDialog campaignId={campaignId} ad={ad} onClose={() => setEditing(false)} />}
    </div>
  );
}

export default function MetaAdsCampaignsPage() {
  const dispatch = useDispatch();
  const { campaigns, loading, connected, adsByCampaign, adSetsByCampaign, adAccounts } = useSelector((s) => s.metaAds);
  const [showWizard, setShowWizard] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [renaming, setRenaming] = useState(null); // campaign being renamed
  const [editingAdSet, setEditingAdSet] = useState(null); // { campaign, adSet }
  const [expandedId, setExpandedId] = useState(null);
  const [pageError, setPageError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Pulls campaigns built in Meta Ads Manager into this list and refreshes
  // delivery/review status on every campaign, ad set and ad.
  const importFromMeta = async ({ silent = false } = {}) => {
    if (importing) return;
    setImporting(true);
    if (!silent) { setPageError(null); setImportResult(null); }
    try {
      let total = { campaigns: 0, ads: 0, created: 0 };
      for (const a of adAccounts) {
        const { data } = await api.post('/marketing/meta-ads/import', { adAccountId: a.id });
        const c = data.data?.counts || {};
        total = { campaigns: total.campaigns + (c.campaigns || 0), ads: total.ads + (c.ads || 0), created: total.created + (c.created || 0) };
      }
      if (!silent) setImportResult(total);
      dispatch(fetchCampaigns());
      if (expandedId) { dispatch(fetchAds(expandedId)); dispatch(fetchAdSets(expandedId)); }
    } catch (e) {
      // A background refresh that fails stays quiet — the "Checked with Meta" time
      // simply doesn't move, and the sync button still reports errors when pressed.
      if (!silent) setPageError(e.response?.data?.error || 'Failed to sync campaigns from Meta');
    } finally {
      setImporting(false);
    }
  };

  // Keep delivery status fresh without anyone pressing the sync button: once when
  // the list opens with a stale (or never-checked) campaign, then every 5 minutes
  // while the tab is visible.
  const autoSyncedRef = useRef(false);
  useEffect(() => {
    if (autoSyncedRef.current || !adAccounts.length || !campaigns.length) return;
    const STALE_MS = 10 * 60 * 1000;
    const stale = campaigns.some((c) => c.platformCampaignId && (!c.statusSyncedAt || Date.now() - new Date(c.statusSyncedAt).getTime() > STALE_MS));
    if (stale) { autoSyncedRef.current = true; importFromMeta({ silent: true }); }
  }, [adAccounts, campaigns]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!adAccounts.length) return undefined;
    const timer = setInterval(() => { if (document.visibilityState === 'visible') importFromMeta({ silent: true }); }, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [adAccounts, importing]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { dispatch(fetchCampaigns()); dispatch(fetchAdAccounts()); }, [dispatch]);

  // Meta ad accounts aren't necessarily in USD — this account, for example, is
  // INR. Look up each campaign's own ad account to format spend/CPL in the
  // right currency instead of a hardcoded "$".
  const currencyFor = (adAccountId) => adAccounts.find((a) => a.id === adAccountId)?.currency || 'USD';

  const toggleExpand = (campaign) => {
    const next = expandedId === campaign._id ? null : campaign._id;
    setExpandedId(next);
    if (next) {
      dispatch(fetchAds(campaign._id));
      dispatch(fetchAdSets(campaign._id));
    }
  };

  const toggleStatus = async (campaign) => {
    setBusyId(campaign._id);
    await dispatch(updateCampaign({ id: campaign._id, campaignData: { status: campaign.status === 'active' ? 'paused' : 'active' } }));
    setBusyId(null);
  };

  const remove = async (campaign) => {
    if (!(await alertService.confirm('Delete?', `Delete campaign "${campaign.name}"? This also pauses it on Meta.`, 'danger', 'Delete'))) return;
    setBusyId(campaign._id);
    setPageError(null);
    const result = await dispatch(deleteCampaign(campaign._id));
    if (result.error) setPageError(result.payload || 'Failed to delete campaign');
    setBusyId(null);
  };

  const refreshInsights = async (campaign) => {
    setBusyId(campaign._id);
    await dispatch(fetchCampaignInsights(campaign._id));
    setBusyId(null);
  };

  const rename = (campaign) => setRenaming(campaign);

  const duplicate = async (campaign) => {
    setBusyId(campaign._id);
    setPageError(null);
    const result = await dispatch(duplicateCampaign(campaign._id));
    if (result.error) {
      setPageError(result.payload || 'Failed to duplicate campaign');
    } else if (result.payload?.errors?.length) {
      setPageError(`Campaign duplicated, but some ad sets/ads didn't copy cleanly: ${result.payload.errors.join('; ')}`);
    }
    setBusyId(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-[#5C5C5C]">
          {connected ? `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}` : 'Connect a Meta Ads account in Settings to create campaigns.'}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => importFromMeta()}
            disabled={!connected || importing || adAccounts.length === 0}
            title="Pull in campaigns made in Meta Ads Manager and refresh delivery status" aria-label="Pull in campaigns made in Meta Ads Manager and refresh delivery status"
            className="flex items-center gap-2 bg-white border border-[#D8DDE6] hover:bg-[#F4F6F9] disabled:opacity-50 text-[#2E2E2E] text-[13px] font-semibold px-4 py-2 rounded-[4px] whitespace-nowrap"
          >
            <Download size={15} className={importing ? 'animate-pulse' : ''} /> {importing ? 'Syncing…' : 'Sync from Meta'}
          </button>
          <button
            onClick={() => setShowWizard(true)}
            disabled={!connected}
            className="flex items-center gap-2 bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2 rounded-[4px] whitespace-nowrap"
          >
            <Plus size={15} /> New Campaign
          </button>
        </div>
      </div>

      {pageError && <div className="bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-3 py-2 text-[12px] text-[#8E030F]">{pageError}</div>}
      {importResult && (
        <div className="bg-[#E3F3E8] border border-[#9FD9B4] rounded-[4px] px-3 py-2 text-[12px] text-[#036B3A]">
          Synced {importResult.campaigns} campaign{importResult.campaigns === 1 ? '' : 's'} and {importResult.ads} ad{importResult.ads === 1 ? '' : 's'} from Meta
          {importResult.created ? ` — ${importResult.created} new item${importResult.created === 1 ? '' : 's'} added.` : '.'}
        </div>
      )}

      {loading && campaigns.length === 0 ? (
        <div className="text-center py-16 text-[13px] text-[#5C5C5C]">Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#D8DDE6] rounded-[4px]">
          <Megaphone size={28} className="mx-auto text-[#D8DDE6] mb-2" />
          <p className="text-[13px] text-[#5C5C5C]">No campaigns yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
          <div className="slds2-table-wrap"><table className="w-full text-[13px]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
              <tr>
                <th className="w-8"></th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Objective</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Spend</th>
                <th className="text-right px-4 py-2.5">Leads</th>
                <th className="text-right px-4 py-2.5">CPL</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D8DDE6]">
              {campaigns.map((c) => (
                <React.Fragment key={c._id}>
                  <tr>
                    <td className="px-2">
                      <button onClick={() => toggleExpand(c)} className="text-[#5C5C5C] hover:text-[#2E2E2E]">
                        {expandedId === c._id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-[#2E2E2E]">
                      {c.name}
                      {c.importedFromMeta && <span className="ml-2 text-[10px] font-semibold text-[#5C5C5C] border border-[#D8DDE6] rounded px-1.5 py-0.5 align-middle">From Ads Manager</span>}
                      {(c.recommendations || []).length > 0 && (
                        <div className="mt-1 flex items-start gap-1 text-[11px] text-[#8B5E00] font-normal" title={c.recommendations.map((r) => r.message).join('\n\n')}>
                          <Lightbulb size={12} className="mt-0.5 flex-shrink-0" />
                          <span>Meta: {c.recommendations[0].title}{c.recommendations.length > 1 ? ` (+${c.recommendations.length - 1} more)` : ''}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-[#5C5C5C]">{c.objective}</td>
                    <td className="px-4 py-2.5">
                      <DeliveryBadge effectiveStatus={c.effectiveStatus} fallback={c.status} />
                      <p className="text-[10px] text-[#706E6B] mt-0.5" title="Refresh from Meta with the sync button above">
                        {c.statusSyncedAt ? `Checked with Meta ${new Date(c.statusSyncedAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Not yet checked with Meta'}
                      </p>
                    </td>
                    <td className="px-4 py-2.5 text-right">{c.insights ? formatCurrency(c.insights.spend, currencyFor(c.adAccountId)) : '—'}</td>
                    <td className="px-4 py-2.5 text-right">{c.insights?.conversions ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right">{c.insights?.cpl ? formatCurrency(c.insights.cpl, currencyFor(c.adAccountId)) : '—'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button title="Refresh insights" aria-label="Refresh insights" onClick={() => refreshInsights(c)} disabled={busyId === c._id} className="text-[#5C5C5C] hover:text-[#0176D3] disabled:opacity-40">
                          <RefreshCw size={14} className={busyId === c._id ? 'animate-spin' : ''} />
                        </button>
                        <button title={c.status === 'active' ? 'Pause' : 'Resume'} onClick={() => toggleStatus(c)} disabled={busyId === c._id} className="text-[#5C5C5C] hover:text-[#0176D3] disabled:opacity-40">
                          {c.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                        <button title="Rename" aria-label="Rename" onClick={() => rename(c)} disabled={busyId === c._id} className="text-[#5C5C5C] hover:text-[#0176D3] disabled:opacity-40">
                          <Pencil size={14} />
                        </button>
                        <button title="Duplicate" aria-label="Duplicate" onClick={() => duplicate(c)} disabled={busyId === c._id} className="text-[#5C5C5C] hover:text-[#0176D3] disabled:opacity-40">
                          <Copy size={14} />
                        </button>
                        <button title="Delete" aria-label="Delete" onClick={() => remove(c)} disabled={busyId === c._id} className="text-[#5C5C5C] hover:text-[#BA0517] disabled:opacity-40">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === c._id && (
                    <tr>
                      <td colSpan={8} className="bg-[#FAFBFC] px-0 py-2">
                        <div className="px-4 pb-1 text-[11px] font-semibold text-[#706E6B]">Ads in this campaign</div>
                        {(adsByCampaign[c._id] || []).length === 0 ? (
                          <div className="px-4 py-3 text-[12px] text-[#5C5C5C]">No ads created yet for this campaign.</div>
                        ) : (
                          <>
                            {(adSetsByCampaign[c._id] || []).map((adSet) => {
                              const ads = (adsByCampaign[c._id] || []).filter((ad) => ad.adSetId === adSet._id);
                              if (ads.length === 0) return null;
                              return (
                                <div key={adSet._id}>
                                  <div className="px-4 py-1.5 flex items-center gap-2 bg-white border-t border-[#EAEDF1]">
                                    <span className="text-[11px] font-bold text-[#2E2E2E]">{adSet.name}</span>
                                    <LearningBadge learningStatus={adSet.learningStatus} />
                                    <button type="button" onClick={() => setEditingAdSet({ campaign: c, adSet })} aria-label={`Edit ${(adSet.name || '').trim()}`} title="Edit ad set" className="ml-auto p-1 rounded hover:bg-[#F3F2F2] text-[#706E6B]"><Pencil size={13} /></button>
                                  </div>
                                  {ads.map((ad) => <AdRow key={ad._id} campaignId={c._id} ad={ad} />)}
                                </div>
                              );
                            })}
                            {/* Ungrouped fallback — an ad whose ad set hasn't loaded/synced yet
                                still shows up here instead of silently disappearing. */}
                            {(adsByCampaign[c._id] || [])
                              .filter((ad) => !(adSetsByCampaign[c._id] || []).some((adSet) => adSet._id === ad.adSetId))
                              .map((ad) => <AdRow key={ad._id} campaignId={c._id} ad={ad} />)}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {renaming && <RenameCampaignDialog campaign={renaming} onClose={() => setRenaming(null)} />}
      {editingAdSet && (
        <EditAdSetDialog
          campaignId={editingAdSet.campaign._id}
          adSet={editingAdSet.adSet}
          campaignIsCbo={!!editingAdSet.campaign.isCbo}
          onClose={() => setEditingAdSet(null)}
        />
      )}

      {showWizard && (
        <MetaAdsCampaignWizard
          onClose={() => setShowWizard(false)}
          onDone={() => { setShowWizard(false); dispatch(fetchCampaigns()); }}
        />
      )}
    </div>
  );
}
